"use client";

import { ErrorMessage, Field, Label } from "@/components/catalyst/fieldset";
import { Strong, Text, TextLink } from "@/components/catalyst/text";
import { requestPasswordReset } from "@/lib/auth/client";
import { Heading } from "@/components/catalyst/heading";
import { useAuthForm } from "@/lib/hooks/use-auth-form";
import { Button } from "@/components/catalyst/button";
import { zodResolver } from "@hookform/resolvers/zod";
import { Input } from "@/components/catalyst/input";
import { InfoAlert } from "@/components/info-alert";
import { useTranslations } from "next-intl";
import { useForm } from "react-hook-form";
import { Logo } from "@/components/logo";
import { Suspense } from "react";
import Form from "next/form";
import { z } from "zod";

function getFormSchema(t: ReturnType<typeof useTranslations>) {
  return z.object({
    email: z.email({ message: t("emailValidation") }),
  });
}

function ResetPasswordForm() {
  const t = useTranslations("ResetPassword");
  const { redirectUrl, errorMessage, onSuccess, onError, clearError } =
    useAuthForm();

  const formSchema = getFormSchema(t);
  type ResetPasswordFormValues = z.infer<typeof formSchema>;

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ResetPasswordFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
    },
  });

  const onSubmit = async (values: ResetPasswordFormValues) => {
    clearError();
    return requestPasswordReset(
      {
        email: values.email,
      },
      { onSuccess, onError },
    );
  };

  return (
    <Form
      action="#"
      onSubmit={handleSubmit(onSubmit)}
      className="grid w-full max-w-sm grid-cols-1 gap-8"
    >
      <Logo />
      <Heading>{t("title")}</Heading>
      {errorMessage && <InfoAlert variant="error" message={errorMessage} />}
      <Text>{t("description")}</Text>
      <Field>
        <Label>{t("emailLabel")}</Label>
        <Input type="email" {...register("email")} disabled={isSubmitting} />
        {errors.email && <ErrorMessage>{errors.email.message}</ErrorMessage>}
      </Field>
      <Button
        type="submit"
        className="w-full"
        color="purple"
        disabled={isSubmitting}
      >
        {isSubmitting ? t("sendingResetEmail") : t("resetPassword")}
      </Button>
      <Text>
        {t("dontHaveAccount")}{" "}
        <TextLink
          href={
            redirectUrl
              ? `/register?redirectUrl=${encodeURIComponent(redirectUrl)}`
              : "/register"
          }
        >
          <Strong>{t("signUp")}</Strong>
        </TextLink>
      </Text>
    </Form>
  );
}

export default function ResetPassword() {
  const t = useTranslations("ResetPassword");

  return (
    <Suspense fallback={<div>{t("loading")}</div>}>
      <ResetPasswordForm />
    </Suspense>
  );
}
